/**
 * @fileoverview Pricing & Course Purchase Page - Fixed Pricing Experience
 * @module PricingPage
 * 
 * Design Philosophy:
 * - Consistent with LandingPage brand aesthetic and header design
 * - Clear value proposition with professional tier comparison
 * - Fixed pricing with lifetime access
 * - Mobile-optimized payment flow
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStripe } from '../hooks/useStripe';
import { 
  ArrowLeft,
  Check,
  Star,
  Zap,
  FileText,
  Bot,
  Clock,
  Shield,
  Users,
  Target,
  Sparkles,
  ArrowRight,
  AlertCircle
} from 'lucide-react';

interface PricingTier {
  id: 'standard' | 'ai_enhanced';
  name: string;
  description: string;
  badge?: string;
  price: string;
  features: string[];
  highlight: boolean;
}

const PricingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { createCheckoutSession, loading: stripeLoading, error: stripeError } = useStripe();
  const [processingTier, setProcessingTier] = useState<string | null>(null);

  // Load Montserrat font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  const Logo = () => (
    <div className="flex items-center gap-3">
      <img 
        src="/transparent-640-logo.png" 
        alt="Appeal Your Home" 
        className="w-10 h-10 object-contain"
      />
      <div className="flex flex-col">
        <h1 className="text-lg font-extrabold text-white tracking-wide" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          APPEAL YOUR HOME
        </h1>
        <p className="text-xs text-gray-300 font-medium tracking-wide" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Learn. Advocate. Save.
        </p>
      </div>
    </div>
  );

  const tiers: PricingTier[] = [
    {
      id: 'standard',
      name: 'Standard Course',
      description: 'Complete Florida property tax appeal course with all the essentials',
      price: '$197',
      features: [
        'Complete 7-module video course',
        'Downloadable templates and worksheets',
        'County-specific resources and deadlines',
        'Property assessment tracking tools',
        'Email support',
        '95%+ success rate',
        'Lifetime access to course materials'
      ],
      highlight: false
    },
    {
      id: 'ai_enhanced',
      name: 'AI-Enhanced Course',
      description: 'Everything in Standard plus AI-powered document generation and personalized guidance',
      badge: 'Most Popular',
      price: '$250',
      features: [
        'Everything in Standard course',
        'AI-generated appeal documents',
        'Personalized property analysis',
        'AI-powered comparable property research',
        'Smart deadline reminders',
        'Priority email support',
        'Live Q&A session access',
        '95%+ success rate',
        'Works for all your properties'
      ],
      highlight: true
    }
  ];

  const handleGetStarted = async (tierId?: string) => {
    setProcessingTier(tierId || 'checkout');
    
    try {
      // Use guest checkout if user is not authenticated
      const useGuestCheckout = !user || !user.id;
      
      await createCheckoutSession({
        guestCheckout: useGuestCheckout,
      });
      
      // If we reach here, there was an error (successful checkout redirects away)
      console.error('Checkout completed but did not redirect');
      
    } catch (error) {
      console.error('Checkout error:', error);
      // Error handling - could show a toast notification here
    } finally {
      setProcessingTier(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      {/* Header - Matches LandingPage Navigation */}
      <nav className="sticky top-0 w-full bg-gray-900/95 backdrop-blur-sm shadow-lg z-50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-8">
              <button 
                onClick={() => navigate('/dashboard')}
                className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>Dashboard</span>
              </button>
              <div className="w-px h-6 bg-gray-600"></div>
              <Logo />
            </div>
            <div className="flex items-center space-x-6">
              <div className="text-right">
                <p className="text-white font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>{user?.firstName || 'Student'}</p>
                <p className="text-xs text-gray-300 flex items-center gap-1 justify-end" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <Star className="w-3 h-3 text-blue-400 fill-current" />
                  {user?.tier || 'Standard'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-1 h-8 bg-gradient-to-b from-blue-600 to-blue-700 rounded-full"></div>
            <h1 className="text-4xl font-extrabold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>Get Lifetime Access</h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            One-time purchase, lifetime access. Start saving thousands on your Florida property taxes today.
          </p>
        </div>

        {/* Error Display */}
        {stripeError && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-red-50 border border-red-200 rounded-lg text-red-700 p-4 flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p style={{ fontFamily: 'Montserrat, sans-serif' }}>{stripeError}</p>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={`relative bg-white rounded-2xl shadow-2xl border-2 transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 ${
                tier.highlight
                  ? 'border-blue-600 ring-2 ring-blue-100'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              {/* Popular Badge */}
              {tier.badge && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 rounded-full text-sm font-bold flex items-center space-x-2 shadow-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    <Sparkles className="w-4 h-4" />
                    <span>{tier.badge}</span>
                  </div>
                </div>
              )}

              <div className="p-10">
                {/* Plan Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>{tier.name}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>{tier.description}</p>
                  
                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-end justify-center space-x-1">
                      <span className="text-5xl font-extrabold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {tier.price}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>One-time payment • Lifetime access</p>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleGetStarted(tier.id)}
                    disabled={processingTier === tier.id || stripeLoading}
                    className={`w-full py-4 px-6 rounded-lg font-bold text-lg transition-all duration-300 flex items-center justify-center space-x-2 ${
                      tier.highlight
                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200 border border-gray-300'
                    } ${(processingTier === tier.id || stripeLoading) ? 'opacity-50 cursor-not-allowed' : ''}`} 
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {processingTier === tier.id ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                    ) : (
                      <>
                        <span>Get Lifetime Access</span>
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>

                {/* Features List */}
                <div className="space-y-4">
                  {tier.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        tier.highlight ? 'text-blue-600' : 'text-gray-500'
                      }`} />
                      <span className="text-gray-700 leading-relaxed font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Feature Comparison - AI-Enhanced Highlights */}
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-10 mb-16">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <Bot className="w-8 h-8 text-blue-600" />
              <h2 className="text-3xl font-extrabold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>AI-Enhanced Features</h2>
            </div>
            <p className="text-xl text-gray-600 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>See how AI can supercharge your property tax appeal</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-50 border border-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>AI Document Generation</h3>
              <p className="text-gray-600 leading-relaxed font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Automatically generate professional appeal documents tailored to your properties.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-50 border border-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>Smart Property Analysis</h3>
              <p className="text-gray-600 leading-relaxed font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                AI analyzes your properties to identify the strongest arguments for appeals.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-50 border border-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>Intelligent Research</h3>
              <p className="text-gray-600 leading-relaxed font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Find the best comparable properties and market data automatically.
              </p>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="grid md:grid-cols-3 gap-8 text-center">
          <div className="flex flex-col items-center">
            <Shield className="w-12 h-12 text-green-600 mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>95%+ Success Rate</h3>
            <p className="text-gray-600 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>Proven results across Texas counties</p>
          </div>

          <div className="flex flex-col items-center">
            <Users className="w-12 h-12 text-blue-600 mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Proven Results</h3>
            <p className="text-gray-600 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>Thousands of successful appeals across Texas</p>
          </div>

          <div className="flex flex-col items-center">
            <Clock className="w-12 h-12 text-purple-600 mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Lifetime Access</h3>
            <p className="text-gray-600 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>One payment, lifetime access to all materials</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage; 